import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  Inject,
  inject,
  NgZone,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { PDF_DATA } from '@vsolv/packages/pdf/web';
import { Warranty } from '@wsphere/warranties/domain';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-declaration-pdf',
  templateUrl: './declaration-pdf.component.html',
  styleUrls: ['./declaration-pdf.component.scss'],
  standalone: false,
})
export class DeclarationPDFComponent implements AfterViewInit, OnDestroy {
  @HostBinding('class') protected _class = 'block';

  @ViewChild('overviewPage') private overviewPage?: ElementRef<HTMLElement>;
  @ViewChild('overviewContent') private overviewContent?: ElementRef<HTMLElement>;

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly zone = inject(NgZone);
  private destroyed = false;

  protected readonly today = new Date();
  protected logoLoadError = false;

  // Footer text from tenant configuration or defaults
  protected get page1Footer(): string {
    return this.declaration?.tenantConfig?.page1Footer || '';
  }

  protected get coverageTableFooter(): string {
    return this.declaration?.tenantConfig?.coverageTableFooter || '';
  }

  protected get page2Footer(): string {
    return this.declaration?.tenantConfig?.page2Footer || '';
  }

  protected get providerName(): string {
    return this.declaration?.tenantConfig?.providerName || 'Warranty Provider';
  }

  protected get summaryPlans(): Warranty.CoverageSummaryPlan[] {
    return this.declaration?.coverageSummary?.plans || [];
  }

  protected get summaryRows(): Warranty.CoverageSummaryRow[] {
    return this.declaration?.coverageSummary?.rows || [];
  }

  protected get isSummaryLandscape(): boolean {
    return this.declaration?.coverageSummary?.layout === 'landscape';
  }

  protected onLogoError(): void {
    this.logoLoadError = true;
  }

  constructor(@Inject(PDF_DATA) public declaration: Warranty.DeclarationData) {}

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      window.addEventListener('beforeprint', this.fitOverview);
      void this.prepareLayout();
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    window.removeEventListener('beforeprint', this.fitOverview);
  }

  private async prepareLayout(): Promise<void> {
    this.host.nativeElement.setAttribute('data-pdf-layout-state', 'preparing');
    await document.fonts.ready;
    await Promise.all(
      Array.from(this.host.nativeElement.querySelectorAll('img'))
        .filter(img => !img.complete)
        .map(
          img =>
            new Promise<void>(resolve => {
              const finish = () => {
                clearTimeout(timeout);
                img.removeEventListener('load', finish);
                img.removeEventListener('error', finish);
                resolve();
              };
              const timeout = setTimeout(finish, 3000);
              img.addEventListener('load', finish);
              img.addEventListener('error', finish);
            })
        )
    );
    if (!this.destroyed) {
      this.fitOverview();
    }
  }

  /**
   * Supported content is everything that fits at either of these readable densities.
   * Never scale the whole document, clip fields, or shrink the handwritten signature space.
   * The PDF handler checks this state and rejects excess content before creating a PDF.
   */
  private readonly fitOverview = (): void => {
    const page = this.overviewPage?.nativeElement;
    const content = this.overviewContent?.nativeElement;
    if (!page || !content) {
      return;
    }

    this.host.nativeElement.setAttribute('data-pdf-layout-state', 'preparing');
    const style = getComputedStyle(page);
    const availableHeight = page.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
    for (const density of ['regular', 'compact']) {
      page.setAttribute('data-density', density);
      const fitsVertically = content.getBoundingClientRect().height <= availableHeight;
      const fitsHorizontally = [content, ...Array.from(content.querySelectorAll<HTMLElement>('*'))].every(
        element => element.scrollWidth <= element.clientWidth
      );
      if (fitsVertically && fitsHorizontally) {
        this.host.nativeElement.setAttribute('data-pdf-layout-state', 'ready');
        return;
      }
    }
    this.host.nativeElement.setAttribute('data-pdf-layout-state', 'overflow');
  };
}
